export default (() =>
    class extends wealthify.componentBases.FieldUserDefined {

        constructor (config = {}) {
            const styleSelector = 'c-field-text-base';
            const validationProps = [
                wealthify.FieldValidation.validationPropEnums.Pattern,
                wealthify.FieldValidation.validationPropEnums.MaxLength,
                wealthify.FieldValidation.validationPropEnums.MinLength,
            ];

            const fieldElement = config.fieldElement
                ? config.fieldElement
                : `<input type="${config.type ?? 'text'}" inputmode="${config.inputmode ?? 'text'}" class="${styleSelector}__input" autocomplete="off" data-fieldinput />`;

            const defaultConfig = {
                templateHTML:  `
                    <div class="${styleSelector}__wrapper" data-root>

                        <div data-fieldlabelcontainer>
                            <label class="${styleSelector}__label" data-fieldlabel></label>
                        </div>

                        <p data-fieldlabelpre></p>

                        <div class="${styleSelector}__input-container">
                            <div class="${styleSelector}__controls-container" data-controlscontainer>
                                <div class="${styleSelector}__value-container">
                                    <span class="${styleSelector}__prefix" data-prefix></span>
                                    ${fieldElement}
                                    <span class="${styleSelector}__suffix" data-suffix></span>
                                </div>
                                <span class="${styleSelector}__button u-display__none" data-slot="button" data-button></span>
                            </div>
                            <w-icon class="${styleSelector}__state-icon" size="200" data-statusicon></w-icon>
                            <span data-statusaria></span>
                        </div>

                        <p class="${styleSelector}__label-sub" data-fieldlabelsub></p>
                        <w-message class="${styleSelector}__message" aria-live="assertive" data-fieldmessage></w-message>

                    </div>`,
                cacheDom: ['data-fieldlabelcontainer', 'data-fieldlabel', 'data-fieldlabelpre', 'data-controlscontainer', 'data-fieldinput', 'data-button', 'data-prefix', 'data-suffix', 'data-statusicon', 'data-statusaria', 'data-fieldlabelsub', 'data-fieldmessage'],
            };

            Object.assign(config, defaultConfig);
            super(config);

            this.styleSelectors.add(styleSelector);
            this.constructor.baseValidationProps.push(...validationProps);

            this.addEventListener(this.events.ValueChange.name, this.#applyValueToInput);
            this.addEventListener('input', (event) => {
                // Using the data in the event, since the .value property of input doesn't always retrieve the actual value if it is invalid for the input type
                // e.g. a value of . in a type=number comes back as empty string
                // null values in event.data mean the user has cleared the value, so change this to ''
                const realValue = this.dom.fieldinput.value === '' && event.data !== '' ? event.data ?? '' : this.dom.fieldinput.value;

                this.inputAlreadyUpdated = true;
                this.inputData = {inputType: event.inputType};
                this.value = realValue;
            }, true);
            this.addEventListener(this.events.DisabledToggled.name, this.#disableButton);

            this.onConnectedCallback = () => {
                this.deprecatedAttachAdditionalProps(this.userEntryProps);

                // Adjust width of field to content width when readonly
                // (keeps the suffix from being pushed to the right of the container)
                this.#setReadonlyFieldInputWidth();
                this.addEventListener(this.events.ReadOnlyToggled.name, this.#setReadonlyFieldInputWidth);
                this.addEventListener(this.events.ValueChange.name, this.#setReadonlyFieldInputWidth);

                this.#setupButton();


                this.addEventListener(this.events.DisabledToggled.name, this.#disableButton);

                if(this.validateMaxlength !== undefined) {
                    this.addEventListener(this.events.ValueChange.name, this.#restrictMaxLength.bind(this));
                }

                if(this.mask && this.value) {
                    // Apply mask on predefined value if mask is configured
                    this.#applyMaskingPreCheck();
                }
            };
        }

        // Static properties
        static inputAlreadyUpdated = false;

        // Used to force the widget button to remain hidden in field-text.
        // TODO: to be updated and implemented globally as part of the slot rework
        // PDB1-1008.
        static forceButtonSlotHidden = false;
        static requiredMaskProperties = [
            'delimiter', 'spacing'
        ]

        // Instance properties
        userEntryProps = Object.freeze([
            {
                class: new wealthify.Prop('allowedcharacters', {
                    default: '[^<>]',
                    type: wealthify.propTypes.Regex(),
                    nullable: true,
                }),
                callback: () => this.dom.fieldinput.addEventListener('beforeinput', this.#restrictDisallowedCharacters.bind(this))
            },
            {
                class: new wealthify.Prop('placeholderprop'),
                callback: () => this.#updatePlaceholder()
            },
            {
                class: new wealthify.Prop('prefix', { default: '' }),
                callback: () => this.#updatePrefix()
            },
            {
                class: new wealthify.Prop('suffix', { default: '' }),
                callback: () => this.#updateSuffix()
            },
            {
                class: new wealthify.Prop('mask', {
                    type: wealthify.propTypes.Object(),
                    validators: [wealthify.propValidators.ObjectContainsKeys(this.constructor.requiredMaskProperties)]
                }),
                callback: () => this.#applyMask()
            },
            {
                class: new wealthify.Prop('aria-active-descendant-prop'),
                callback: () => this.#updateActiveDescendantProp()
            },
            {
                class: new wealthify.Prop('aria-expanded-prop'),
                callback: () => this.#updateAriaExpandedProp()
            },
            {
                class: new wealthify.Prop('aria-controls-prop'),
                callback: () => this.#updateAriaControlsProp()
            },

            {
                class: new wealthify.Prop('aria-role-prop'),
                callback: () => this.#updateAriaRoleProp()
            },
        ]);

        // Private methods
        #maskingInProgress = false;

        #applyValueToInput () {
            if (this.inputAlreadyUpdated || this.dom.fieldinput.value == this.value) {
                // Callback called after input event, or values are the same
                // no need to update input element
                this.inputAlreadyUpdated = false;

                return;
            }

            const clonedInput =  this.dom.fieldinput.cloneNode(true);

            clonedInput.value = this.value;

            // The .value property comes back with empty string if the value is invalid for the input type
            const isValueInvalidForInput = clonedInput.value === '' && this.value !== '';

            if (isValueInvalidForInput) {
                // Revert to previous value
                this.value = this.dom.fieldinput.value;
            } else {
                // Apply new value
                this.dom.fieldinput.value = this.value;
            }
        }

        #restrictDisallowedCharacters (event) {
            if (event.data === null || this.allowedcharacters === null) {
                return;
            }

            const hasDisallowedCharacters = this.#hasDisallowedCharacters(event.data);

            if (hasDisallowedCharacters) {
                event.preventDefault();
                event.stopPropagation();

                if (this.mask) {
                    const valueWithoutDelimiter = event.data.replaceAll(this.mask.delimiter, '');
                    const isValueValidWithoutDelimiter = !this.#hasDisallowedCharacters(valueWithoutDelimiter);

                    if (isValueValidWithoutDelimiter) {
                        // Delimiter is the only "invalid" character
                        // so replay the event with the delimiters removed
                        this.value = valueWithoutDelimiter;
                    }
                }
            }
        }

        #hasDisallowedCharacters (value) {
            const regexEnclosedBySquareBrackets = new RegExp(/^\[.+\]$/);

            let allowedCharacters = this.allowedcharacters;

            if (!regexEnclosedBySquareBrackets.test(this.allowedcharacters)) {
                allowedCharacters = `[${allowedCharacters}]`;
            }

            allowedCharacters = `^${allowedCharacters}+$`;

            const regexAllowedCharacters = new RegExp(allowedCharacters);

            return !regexAllowedCharacters.test(value);
        }

        #setupButton() {
            if (this.dom.button.tagName.toLowerCase() === 'span') {
                return;
            }

            if (this.dom.button.tagName.toLowerCase() !== 'w-widget-button') {
                return wealthify.error.throwError(this.tagname, `The slot content for "button" in the "${this.tagName}" component can only be the "w-widget-button" component.`);
            }

            if (!this.forceButtonSlotHidden){
                this.dom.button.classList.remove('u-display__none');
            }
        }

        #restrictMaxLength (event) {
            const value = this.mask ? this.#getMaskedValue(this.value) : this.value;

            const overflowAmount = this.#getMaxLengthOverflow(value);

            if (overflowAmount <= 0) {
                return;
            }

            event.target.value = this.value = value.slice(0, -overflowAmount);
        }

        #disableButton() {
            if (!this.dom.button) {
                return;
            }

            this.dom.button.disabledprop = this.disabledprop;
        }

        #updatePlaceholder() {
            this.dom.fieldinput.placeholder = this.placeholderprop;
        }

        #updatePrefix() {
            this.#updatePrefixSuffix(this.prefix, this.dom.prefix);
        }

        #updateSuffix() {
            this.#updatePrefixSuffix(this.suffix, this.dom.suffix);
        }

        #applyMask () {
            // For masking to work, the input type must be text
            // since selection is not allowed on certain input types, e.g. number
            this.dom.fieldinput.type = 'text';

            this.addEventListener(this.events.ValueChange.name, this.#applyMaskingPreCheck);

            this.dom.fieldinput.addEventListener('click', this.#setCursorPositionOnClick.bind(this));
            this.dom.fieldinput.addEventListener('mouseup', this.#setCursorPositionOnMouseUp.bind(this));

            this.addEventListener('keydown', this.#setCursorPositionOnArrowKeys);
        }

        #updateActiveDescendantProp(){
            if(!this.ariaActiveDescendantProp){
                return;
            }
    
            this.dom.fieldinput.setAttribute('aria-activedescendant', this.ariaActiveDescendantProp);
        }

        #updateAriaExpandedProp(){
            if(!this.ariaExpandedProp){
                return;
            }
    
            this.dom.fieldinput.setAttribute('aria-expanded', this.ariaExpandedProp);
        }
    
        #updateAriaControlsProp(){
            if(!this.ariaControlsProp){
                return;
            }
    
            this.dom.fieldinput.setAttribute('aria-controls', this.ariaControlsProp);
        }

        #updateAriaRoleProp(){
            if(!this.ariaRoleProp){
                return;
            }
    
            this.dom.fieldinput.setAttribute('role', this.ariaRoleProp);
        }

        #applyMaskingPreCheck (event = {}) {
            const position = this.dom.fieldinput.selectionStart;
            const wasDeleteKeyPressed = event?.detail?.inputType === 'deleteContentForward';
            const wasDelimiterRemoved = this.#isSelectionOnDelimiter(position + 1);

            if (wasDeleteKeyPressed && wasDelimiterRemoved) {
                // Remove character after delimiter that was deleted
                this.#applyMaskedValue(this.value.substr(0, position) + this.value.substr(position + 1));
            } else if (this.#getMaxLengthOverflow(this.value) <= 0) {
                // Only mask value if max length not exceeded, since value will need to be set again anyway
                this.#applyMaskedValue();
            }
        }

        #applyMaskedValue (value = this.value) {
            if (this.#maskingInProgress) {
                return;
            }

            const position = this.dom.fieldinput.selectionStart;
            const unmaskedValue = value;

            // The #maskingInProgress flag required to avoid this method firing again
            this.#maskingInProgress = true;
            this.value = this.#getMaskedValue(value);
            this.#setCursorPositionOnValueChange(unmaskedValue, position);

            this.#maskingInProgress = false;
        }


        #getMaskedValue (value) {
            const spacings = this.mask.spacing.split(',').map(Number);
            const rawValue = value.replaceAll(this.mask.delimiter, '');

            let maskedValue = '';

            if (spacings.length === 1) {
                maskedValue = rawValue.replace(new RegExp(`(\\w{${this.mask.spacing}})(?!\\s|$|-)`, 'g'), `$1${this.mask.delimiter}`);
            } else {
                let currentPosition = 0;

                spacings.forEach((spacing, index) => {
                    if (currentPosition < rawValue.length) {
                        const section = rawValue.substr(currentPosition, spacing);

                        maskedValue += (index > 0 ? this.mask.delimiter : '') + section;
                        currentPosition += spacing;
                    }
                });
            }

            return maskedValue;
        }

        #isSelectionOnDelimiter (start, end) {
            let spacings = this.mask.spacing.split(',').map(Number);

            if (spacings.length === 1) {
                // Create array with same one value
                // so that delimiter positions can be evaluated
                const rawValue = this.value.replaceAll(this.mask.delimiter, '');
                const arrayLength = Math.ceil(rawValue.length / this.mask.spacing);

                spacings = new Array(arrayLength).fill(Number(this.mask.spacing));
            }

            const delimiterPositions = spacings.slice(0, -1).reduce((array, current, index) => {
                const previous = array[index - 1] ?? 0;

                array.push(previous + current + 1 );

                return array;
            }, []);

            const isStartOnDelimiter = start !== 0 && delimiterPositions.includes(Number(start));
            const isEndOnDelimiter = end && delimiterPositions.includes(Number(end));

            return isStartOnDelimiter || isEndOnDelimiter;
        }

        #getMaxLengthOverflow (value) {
            if (!this.validateMaxlength) {
                return 0;
            }

            value = this.mask ? this.#getMaskedValue(value) : value;

            return value.length - this.validateMaxlength;
        }

        #setCursorPositionOnValueChange (unmaskedValue, position) {
            const unmaskedValueBeforeCursor = unmaskedValue.substr(0, position);
            const newPosition = this.#getMaskedValue(unmaskedValueBeforeCursor).length;

            this.dom.fieldinput.setSelectionRange(newPosition, newPosition);
        }

        #setCursorPositionOnClick (event) {
            if (!this.#isSelectionOnDelimiter(event.target.selectionStart)) {
                return;
            }

            let position = --event.target.selectionStart;

            this.dom.fieldinput.setSelectionRange(position, position);
        }

        #setCursorPositionOnMouseUp () {
            const start = this.dom.fieldinput.selectionStart;
            const end = this.dom.fieldinput.selectionEnd;

            if (start !== end) {
                // Selection made

                if (this.value.substr(end - 1, 1) === this.mask.delimiter) {
                    // Move end if delimiter selected
                    this.dom.fieldinput.setSelectionRange(start, end - 1);
                }

                if (this.#isSelectionOnDelimiter(start)) {
                    // Move start if on delimiter
                    this.dom.fieldinput.setSelectionRange(start - 1, end);
                }
            }
        }

        #setCursorPositionOnArrowKeys (event) {
            const isLeft = event.keyCode === 37;
            const isRight = event.keyCode === 39;

            if (!isLeft && !isRight) {
                return;
            }

            const isSelecting = event.shiftKey;
            const positions = {
                start: event.target.selectionStart,
                end: event.target.selectionEnd
            };
            const moveStart = () => isLeft ? --positions.start : ++positions.start;
            const moveEnd = () => isLeft ? --positions.end : ++positions.end;

            if (isSelecting) {
                if (event.target.selectionDirection === 'forward') {
                    moveEnd();
                } else {
                    moveStart();
                }

                if (this.#isSelectionOnDelimiter(positions.start, positions.end)) {
                    event.target.selectionStart = positions.start;
                    event.target.selectionEnd = positions.end;
                }
            } else {
                // If not making a selection, then always use start
                moveStart();

                if (this.#isSelectionOnDelimiter(positions.start)) {
                    event.target.setSelectionRange(positions.start, positions.start);
                }
            }
        }

        #updatePrefixSuffix(value, element) {
            const classListAction = value === '' ? 'add' : 'remove';

            element.innerHTML = value;
            element.classList[classListAction]('u-display__none');
        }

        #setReadonlyFieldInputWidth() {
            this.dom.fieldinput.style.width = this.readonlyprop ? this.readOnlyWidth ?? `${this.value?.toString()?.length}ch` : '';
        }
    }
);